import React, { useEffect, useState } from 'react';
import { getRequest, postRequest, showMessage } from '../../../config';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';

const QUESTION_TYPES = [
    { value: 'yesNo', label: 'Yes / No' },
    { value: 'D/ND', label: 'D / ND' },
    { value: '1to5', label: '1 to 5' },
    { value: 'Date', label: 'Date' },
    { value: 'YN/F/T', label: 'YN/F/T' },
    { value: 'DropDown', label: 'DropDown' },
    { value: 'Radio', label: 'Radio' },
    { value: 'Multi', label: 'Multi' },
];

// isGroup = parent checkbox that drives both toolboxPolicyMaker + toolboxAnalyser together.
const CHECKBOX_ROWS = [
    { field: 'files', label: 'Files' },
    { field: 'collaborate', label: 'Collaborate' },
    { field: 'toolboxGroup', label: 'Toolbox', isGroup: true },
    { field: 'toolboxPolicyMaker', label: 'Policy Maker', indent: true },
    { field: 'toolboxAnalyser', label: 'Analyser', indent: true },
];

const S = {
    wrap: { padding: '1.5rem', background: '#f5f6fa', minHeight: 120 },
    card: { background: '#fff', borderRadius: 12, border: '1px solid #e4e8f5', boxShadow: '0 2px 8px rgba(74,103,255,0.06)', overflow: 'hidden' },
    cardHead: { padding: '18px 24px 14px', borderBottom: '1px solid #f0f2ff' },
    title: { fontSize: '1.05rem', fontWeight: 700, color: '#1a1d2e', margin: 0 },
    subtitle: { fontSize: '0.8rem', color: '#7c83a0', marginTop: 4 },
    tableWrap: { overflowX: 'auto', overflowY: 'auto', maxHeight: '70vh', width: '100%', WebkitOverflowScrolling: 'touch' },
    thead: { background: '#f8f9ff' },
    th: { padding: '10px 14px', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#7c83a0', textAlign: 'left', borderBottom: '2px solid #e8eaf5', whiteSpace: 'nowrap' },
    thCenter: { padding: '10px 14px', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#7c83a0', textAlign: 'center', borderBottom: '2px solid #e8eaf5', whiteSpace: 'nowrap' },
    modRow: { background: 'linear-gradient(90deg,#f0f3ff 0%,#f8f9ff 100%)', cursor: 'pointer', borderTop: '2px solid rgb(36, 62, 207)', userSelect: 'none' },
    modTd: { padding: '10px 16px' },
    modColTd: { padding: '6px 14px', textAlign: 'center', verticalAlign: 'middle', borderLeft: '1px solid #e0e4f0' },
    modAccent: { width: 3, height: 34, background: '#4A67FF', borderRadius: 2, flexShrink: 0 },
    modName: { fontSize: '0.88rem', fontWeight: 700, color: '#1a1d2e' },
    modKey: { fontSize: '0.68rem', fontWeight: 700, color: '#4A67FF', background: '#eef0ff', padding: '2px 8px', borderRadius: 10, flexShrink: 0 },
    modMeta: { fontSize: '0.72rem', color: '#9aa0b8' },
    selectAllLabel: { display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 11, color: '#4A67FF', fontWeight: 600, userSelect: 'none' },
    secRow: { background: '#fff', transition: 'background .1s' },
    secTd: { padding: '10px 12px 10px 32px', borderBottom: '1px solid #d0d5e8' },
    secNo: { fontSize: '0.68rem', fontWeight: 700, color: '#4A67FF', background: '#eef0ff', padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap', flexShrink: 0 },
    secName: { fontSize: '0.82rem', color: '#374151' },
    qtTd: { textAlign: 'left', padding: '10px 14px', verticalAlign: 'top', borderLeft: '1px solid #eaecf5', borderBottom: '1px solid #d0d5e8' },
    cb: { width: 13, height: 13, accentColor: '#4A67FF', cursor: 'pointer', flexShrink: 0, marginTop: 1 },
    cbLabel: { display: 'flex', alignItems: 'flex-start', gap: 5, cursor: 'pointer', fontSize: 11, color: '#374151', lineHeight: '16px', userSelect: 'none' },
    cbLabelGroup: { display: 'flex', alignItems: 'flex-start', gap: 5, cursor: 'pointer', fontSize: 11, color: '#4A67FF', fontWeight: 600, lineHeight: '16px', userSelect: 'none', marginTop: 4 },
};

const ToolboxManagement = () => {
    const [tempModules, setTempModules] = useState([]);
    const [openAccordions, setOpenAccordions] = useState({});
    const [loading, setLoading] = useState(false);
    // Tracks the one section row that may have active selections: { moduleId, sectionNo }
    const [activeRow, setActiveRow] = useState(null);

    const fetchModules = async () => {
        setLoading(true);
        try {
            const res = await getRequest('/modules/toolbox/temp-modules', 'not-showMessage');
            if (res?.code === 200) setTempModules(res.body || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchModules(); }, []);

    const toggleAccordion = (moduleId) =>
        setOpenAccordions(prev => ({ [moduleId]: !prev[moduleId] }));

    const isSameRow = (moduleId, sectionNo) =>
        activeRow && String(activeRow.moduleId) === String(moduleId) && activeRow.sectionNo === sectionNo;

    // Wipes a section row in local state.
    const clearRowInState = (prevModuleId, prevSectionNo) => {
        setTempModules(prev => prev.map(mod => {
            if (String(mod._id) !== String(prevModuleId)) return mod;
            return {
                ...mod,
                sections: mod.sections.map(sec => {
                    if (sec.sectionNo !== prevSectionNo) return sec;
                    return { ...sec, toolboxConfig: {} };
                }),
            };
        }));
    };

    // Single-field toggle — enforces single-row constraint on check.
    const handleAssign = async ({ moduleId, sectionNo, questionType, field, assign }) => {
        const apiCalls = [
            postRequest('/modules/toolbox/assign', { moduleId, sectionNo, questionType, field, assign }),
        ];
        // Switching to a new row: clear the previous one first.
        if (assign && activeRow && !isSameRow(moduleId, sectionNo)) {
            clearRowInState(activeRow.moduleId, activeRow.sectionNo);
            apiCalls.push(postRequest('/modules/toolbox/clear-section', { moduleId: activeRow.moduleId, sectionNo: activeRow.sectionNo }));
        }
        // Optimistic state update for the new checkbox.
        setTempModules(prev => prev.map(mod => {
            if (String(mod._id) !== String(moduleId)) return mod;
            return {
                ...mod,
                sections: mod.sections.map(sec => {
                    if (sec.sectionNo !== sectionNo) return sec;
                    const cfg = { ...(sec.toolboxConfig || {}) };
                    cfg[questionType] = { ...(cfg[questionType] || {}), [field]: assign };
                    return { ...sec, toolboxConfig: cfg };
                }),
            };
        }));
        if (assign) setActiveRow({ moduleId, sectionNo });
        try {
            await Promise.all(apiCalls);
            showMessage('Updated.');
        } catch (err) {
            console.error(err);
        }
    };

    // Toolbox group — sets both children; enforces single-row constraint on check.
    const handleToolboxGroup = async ({ moduleId, sectionNo, questionType, assign }) => {
        const apiCalls = [
            postRequest('/modules/toolbox/assign', { moduleId, sectionNo, questionType, field: 'toolboxPolicyMaker', assign }),
            postRequest('/modules/toolbox/assign', { moduleId, sectionNo, questionType, field: 'toolboxAnalyser', assign }),
        ];
        if (assign && activeRow && !isSameRow(moduleId, sectionNo)) {
            clearRowInState(activeRow.moduleId, activeRow.sectionNo);
            apiCalls.push(postRequest('/modules/toolbox/clear-section', { moduleId: activeRow.moduleId, sectionNo: activeRow.sectionNo }));
        }
        setTempModules(prev => prev.map(mod => {
            if (String(mod._id) !== String(moduleId)) return mod;
            return {
                ...mod,
                sections: mod.sections.map(sec => {
                    if (sec.sectionNo !== sectionNo) return sec;
                    const cfg = { ...(sec.toolboxConfig || {}) };
                    cfg[questionType] = { ...(cfg[questionType] || {}), toolboxPolicyMaker: assign, toolboxAnalyser: assign };
                    return { ...sec, toolboxConfig: cfg };
                }),
            };
        }));
        if (assign) setActiveRow({ moduleId, sectionNo });
        try {
            await Promise.all(apiCalls);
            showMessage('Toolbox updated.');
        } catch (err) {
            console.error(err);
        }
    };

    // Module-level column Select All — sets/clears all 4 fields for every section in that column.
    const handleSelectAll = async ({ moduleId, questionType, assign }) => {
        setTempModules(prev => prev.map(mod => {
            if (String(mod._id) !== String(moduleId)) return mod;
            return {
                ...mod,
                sections: mod.sections.map(sec => {
                    const cfg = { ...(sec.toolboxConfig || {}) };
                    cfg[questionType] = { files: assign, collaborate: assign, toolboxPolicyMaker: assign, toolboxAnalyser: assign };
                    return { ...sec, toolboxConfig: cfg };
                }),
            };
        }));
        try {
            await postRequest('/modules/toolbox/assign-column', { moduleId, questionType, assign });
            showMessage(assign ? 'All sections selected.' : 'All sections cleared.');
        } catch (err) {
            console.error(err);
        }
    };

    if (loading) return <div className="df jcc aic p2rem" style={{ color: '#7c83a0' }}>Loading…</div>;

    const SECTION_COL_PX = 260;
    const QT_COL_PX = 120;
    const tableWidth = Math.max(SECTION_COL_PX + QUESTION_TYPES.length * QT_COL_PX, 600);

    return (
        <div style={S.wrap}>
            <div style={S.card}>
                <div style={S.cardHead}>
                    <p style={S.title}>Toolbox Management</p>
                    <p style={S.subtitle}>Configure toolbox options per question type for each section. Only one section row may have active selections at a time.</p>
                </div>
                <div style={S.tableWrap}>
                    <table style={{ width: tableWidth, minWidth: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                        <colgroup>
                            <col style={{ width: SECTION_COL_PX }} />
                            {QUESTION_TYPES.map(qt => <col key={qt.value} style={{ width: QT_COL_PX }} />)}
                        </colgroup>
                        <thead style={S.thead}>
                            <tr>
                                <th style={{ ...S.th, position: 'sticky', left: 0, top: 0, zIndex: 4, backgroundColor: '#f8f9ff', boxShadow: '4px 0 10px -2px rgba(26,29,46,0.12)' }}>
                                    Section
                                </th>
                                {QUESTION_TYPES.map(qt => (
                                    <th key={qt.value} style={{ ...S.thCenter, position: 'sticky', top: 0, zIndex: 3, backgroundColor: '#f8f9ff' }}>
                                        {qt.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {tempModules.map(mod => {
                                const isOpen = !!openAccordions[mod._id];
                                return (
                                    <React.Fragment key={mod._id}>
                                        <tr
                                            style={S.modRow}
                                            onClick={() => toggleAccordion(mod._id)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && toggleAccordion(mod._id)}
                                        >
                                            <td style={{ ...S.modTd, position: 'sticky', left: 0, zIndex: 2, background: 'linear-gradient(90deg,#f0f3ff 0%,#f8f9ff 100%)', boxShadow: '4px 0 10px -2px rgba(26,29,46,0.08)' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <div style={S.modAccent} />
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={S.modName}>{mod.moduleName}</div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                                                            {mod.moduleKey && <span style={S.modKey}>{mod.moduleKey}</span>}
                                                            <span style={S.modMeta}>{mod.sections?.length || 0} section{mod.sections?.length !== 1 ? 's' : ''}</span>
                                                        </div>
                                                    </div>
                                                    {isOpen
                                                        ? <KeyboardArrowUp style={{ color: '#4A67FF', fontSize: 20, flexShrink: 0 }} />
                                                        : <KeyboardArrowDown style={{ color: '#4A67FF', fontSize: 20, flexShrink: 0 }} />
                                                    }
                                                </div>
                                            </td>
                                            {QUESTION_TYPES.map(qt => {
                                                const sections = mod.sections || [];
                                                const allSel = sections.length > 0 && sections.every(sec => {
                                                    const cfg = (sec.toolboxConfig || {})[qt.value] || {};
                                                    return cfg.files && cfg.collaborate && cfg.toolboxPolicyMaker && cfg.toolboxAnalyser;
                                                });
                                                return (
                                                    <td key={qt.value} style={S.modColTd} onClick={e => e.stopPropagation()}>
                                                        <label style={S.selectAllLabel}>
                                                            <input
                                                                type="checkbox"
                                                                style={S.cb}
                                                                checked={allSel}
                                                                onChange={e => handleSelectAll({ moduleId: mod._id, questionType: qt.value, assign: e.target.checked })}
                                                            />
                                                            Select All
                                                        </label>
                                                    </td>
                                                );
                                            })}
                                        </tr>

                                        {isOpen && (mod.sections || []).map(sec => (
                                            <tr key={`${mod._id}-${sec.sectionNo}`} style={S.secRow}>
                                                <td style={{ ...S.secTd, position: 'sticky', left: 0, zIndex: 2, backgroundColor: '#fff', boxShadow: '4px 0 10px -2px rgba(26,29,46,0.08)' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <span style={S.secNo}>{sec.sectionNo}</span>
                                                        <span style={S.secName}>{sec.sectionName}</span>
                                                    </div>
                                                </td>
                                                {QUESTION_TYPES.map(qt => {
                                                    const cfg = (sec.toolboxConfig || {})[qt.value] || {};
                                                    const toolboxGroupChecked = !!(cfg.toolboxPolicyMaker && cfg.toolboxAnalyser);
                                                    return (
                                                        <td key={qt.value} style={S.qtTd}>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                                {CHECKBOX_ROWS.map((row, ri) => {
                                                                    const id = `cb-${mod._id}-${sec.sectionNo}-${qt.value}-${row.field || ri}`;
                                                                    if (row.isGroup) {
                                                                        return (
                                                                            <label key="toolboxGroup" htmlFor={id} style={S.cbLabelGroup}>
                                                                                <input
                                                                                    id={id}
                                                                                    type="checkbox"
                                                                                    style={S.cb}
                                                                                    checked={toolboxGroupChecked}
                                                                                    onChange={e => handleToolboxGroup({
                                                                                        moduleId: mod._id,
                                                                                        sectionNo: sec.sectionNo,
                                                                                        questionType: qt.value,
                                                                                        assign: e.target.checked,
                                                                                    })}
                                                                                />
                                                                                {row.label}
                                                                            </label>
                                                                        );
                                                                    }
                                                                    return (
                                                                        <label
                                                                            key={row.field}
                                                                            htmlFor={id}
                                                                            style={{ ...S.cbLabel, paddingLeft: row.indent ? 10 : 0 }}
                                                                        >
                                                                            <input
                                                                                id={id}
                                                                                type="checkbox"
                                                                                style={S.cb}
                                                                                checked={!!cfg[row.field]}
                                                                                onChange={e => handleAssign({
                                                                                    moduleId: mod._id,
                                                                                    sectionNo: sec.sectionNo,
                                                                                    questionType: qt.value,
                                                                                    field: row.field,
                                                                                    assign: e.target.checked,
                                                                                })}
                                                                            />
                                                                            {row.label}
                                                                        </label>
                                                                    );
                                                                })}
                                                            </div>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ToolboxManagement;
